import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  Modal,
  Popover,
  Button,
  useMediaQuery,
} from '@mui/material';
import { Add, MoreHoriz, Close, KeyboardArrowDown } from '@mui/icons-material';
import { API_BASE } from '../../config/env';
import { httpService } from '../../utility/httpService';
import { httpConstants } from '../../constants';
import ShowToast, { ToastTypeEnum } from '../../common/components/ShowToast';
import { formatTimestamp } from '../../common/utils';
import { getFieldKeyFromCategory } from '../NewIdeationXPLR/NewIdeation/constants';
import SearchModal from './SearchModal';

interface IdeationIntake {
  id: string;
  type: string;
  use_case_id: string;
  conversation_id: string;
  created_by_name: string;
  version: string;
  function: string;
  process: string;
  ideation_name: string;
  level2_name: string;
}

interface ChatSession {
  id: string;
  use_case_title: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  usedInProcess: string;
  conversation_id: string;
  sessionName?: string;
  ideation_intake: IdeationIntake[];
  field_type?: string;
}

interface ChatListingSidebarProps {
  sessions: ChatSession[];
  currentChatId: string;
  onSelectChat: (chatId: string, fieldType?: string) => void;
  onNewChat: () => void;
  onRename?: (sessionId: string, newTitle: string) => void;
  onDelete?: (sessionId: string) => void;
  onNewIdeation: () => void;
  onCategoryChange?: (category: string) => void;
  onSearchChange?: (searchQuery: string) => void;
  onRefetch?: () => void;
  selectedCategory?: string;
  setuseCaseDetails?: (details: Record<string, any>) => void;
  useCaseDetails?: Record<string, any>;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  hasReachedEnd?: boolean;
  searchFromQuery?: string;
  sortBy?: string;
  setSortBy?: (sort: string) => void;
  onSortChange?: (sortBy: string) => void;
}

const ChatListingSidebar: React.FC<ChatListingSidebarProps> = ({
  sessions,
  currentChatId,
  onSelectChat,
  onNewChat,
  searchFromQuery,
  onNewIdeation,
  onCategoryChange,
  onSearchChange,
  onRefetch,
  selectedCategory = '',
  setuseCaseDetails,
  useCaseDetails,
  onLoadMore,
  isLoadingMore = false,
  hasReachedEnd = false,
  sortBy = '',
  setSortBy = () => {},
  onSortChange,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchFromQuery || '');
  const [openSearchModal, setOpenSearchModal] = useState(false);
  const [hoveredSessionId, setHoveredSessionId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSession, setSelectedSession] = useState<ChatSession | null>(null);
  const [modalSession, setModalSession] = useState<ChatSession | null>(null);
  const [openRenameModal, setOpenRenameModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [category, setCategory] = useState(selectedCategory);
  const [intakeAnchorEl, setIntakeAnchorEl] = useState<null | HTMLElement>(null);
  const [hoveredIntakes, setHoveredIntakes] = useState<IdeationIntake[]>([]);
  const intakeCloseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };
  const isMobile = useMediaQuery('(max-width:900px)');
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, session: ChatSession) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedSession(session);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSession(null);
  };

  const handleRename = (event: any) => {
    event?.stopPropagation?.();
    if (selectedSession) {
      setModalSession(selectedSession);
      setRenameValue(selectedSession.use_case_title);
      setOpenRenameModal(true);
    }
    handleMenuClose();
  };

  const handleDelete = (event: any) => {
    event?.stopPropagation?.();
    if (selectedSession) {
      setModalSession(selectedSession);
      setOpenDeleteModal(true);
    }
    handleMenuClose();
  };

  const handleRenameConfirm = async () => {
    console.log('handleRenameConfirm called', modalSession, renameValue);
    if (modalSession && renameValue.trim()) {
      const organizationId = localStorage.getItem('current_organization') || '';
      const url = `${API_BASE}/api/v1/ideation/query-history/${modalSession.conversation_id}/session-name/`;
      console.log('Rename API call:', url, {
        session_name: renameValue.trim(),
        organization_id: organizationId,
        field_type: getFieldKeyFromCategory[selectedCategory],
      });

      try {
        const response = await httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, {
          session_name: renameValue.trim(),
          organization_id: organizationId,
          field_type: modalSession?.field_type,
        });
        console.log('Rename API response:', response);

        if (setuseCaseDetails && useCaseDetails && modalSession.conversation_id === currentChatId) {
          setuseCaseDetails({ ...useCaseDetails, title: renameValue.trim() });
        }

        if (onRefetch) {
          console.log('Calling onRefetch');
          onRefetch();
        }
        setOpenRenameModal(false);
        setRenameValue('');
      } catch (error) {
        console.error('Failed to rename session:', error);
      }
    } else {
      console.log('Closing rename modal - no session or empty name');
      setOpenRenameModal(false);
      setRenameValue('');
    }
  };
  const capitalizeName = (name: string) => {
    if (!name) return name;
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleDeleteConfirm = async () => {
    console.log('handleDeleteConfirm called', modalSession);
    if (modalSession) {
      const organizationId = localStorage.getItem('current_organization') || '';
      const url = `${API_BASE}/api/v1/ideation/query-history/${modalSession.conversation_id}/delete/`;
      console.log('Delete API call:', url);

      try {
        const response = await httpService<any>(httpConstants.METHOD_TYPE.DELETE, url, {
          organization_id: organizationId,
          field_type: modalSession?.field_type,
        });
        console.log('Delete API response:', response);
        if (onRefetch) {
          console.log('Calling onRefetch');
          onRefetch();
        }
        setOpenDeleteModal(false);
        if (modalSession.conversation_id === currentChatId) {
          onNewChat();
          onNewIdeation();
        }
        ShowToast({ message: 'Session deleted successfully', type: ToastTypeEnum.Success });
      } catch (error) {
        console.error('Failed to delete session:', error);
      }
    } else {
      console.log('Closing delete modal - no session');
      ShowToast({ message: 'Failed to delete session', type: ToastTypeEnum.Error });

      setOpenDeleteModal(false);
    }
  };

  const handleIntakeHover = (event: React.MouseEvent<HTMLElement>, intakes: IdeationIntake[]) => {
    if (intakes && intakes.length > 0) {
      if (intakeCloseTimeoutRef.current) {
        clearTimeout(intakeCloseTimeoutRef.current);
        intakeCloseTimeoutRef.current = null;
      }
      setIntakeAnchorEl(event.currentTarget);
      setHoveredIntakes(intakes);
    }
  };

  const handleIntakeClose = () => {
    intakeCloseTimeoutRef.current = setTimeout(() => {
      setIntakeAnchorEl(null);
      setHoveredIntakes([]);
    }, 100);
  };

  const handleIntakePopoverEnter = () => {
    if (intakeCloseTimeoutRef.current) {
      clearTimeout(intakeCloseTimeoutRef.current);
      intakeCloseTimeoutRef.current = null;
    }
  };

  const collapsedWidth = 56;
  const expandedWidth = 350;

  useEffect(() => {
    setCategory(selectedCategory);
  }, [selectedCategory]);

  // Handle scroll detection for infinite scroll
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || isCollapsed || !onLoadMore || isLoadingMore) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      // Trigger load more when user is 200px from bottom
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        onLoadMore();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [onLoadMore, isLoadingMore, isCollapsed]);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onSearchChange) {
        onSearchChange(searchQuery);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || isCollapsed || !currentChatId) return;
    const selectedEl = container.querySelector(
      `[data-conversation-id="${currentChatId}"]`
    ) as HTMLElement | null;
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
    }
  }, [currentChatId, sessions, isCollapsed]);

  const mapProcessName = useMemo(
    () => (processName: string) => {
      if (!processName) return processName;
      return processName
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },
    []
  );
  useEffect(() => {
    setIsCollapsed(isMobile);
  }, [isMobile]);
  return (
    <Box
      sx={{
        width: isCollapsed ? collapsedWidth : expandedWidth,
        height: '100%',
        backgroundColor: '#ffffff',
        borderRight: '1px solid #EBEBEB',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s ease',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* Header Section */}
      {!isCollapsed ? (
        <Box sx={{ px: 2, pt: 3, pb: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 700,
                color: '#000000',
                fontSize: '24px',
              }}
            >
              Data XPLR
            </Typography>

            {/* Create New Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                sx={{
                  backgroundColor: '#E4EBFF',
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  borderRadius: '10px',
                  textTransform: 'capitalize',
                  '&:hover': {
                    backgroundColor: '#eff6ff',
                  },
                }}
                onClick={() => {
                  onNewChat();
                  onNewIdeation();
                }}
              >
                <img
                  src="/images/add-new.png"
                  alt="Add"
                  width={13}
                  style={{ marginRight: '4px' }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, fontSize: '14px', color: '#000000' }}
                >
                  New
                </Typography>
              </Button>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#DDDFDE',
                  },
                  '&:active': {
                    backgroundColor: '#DDDFDE',
                  },
                }}
                onClick={() => setOpenSearchModal(true)}
              >
                <img src="/images/search-icon.png" alt="Search" width={20} height={20} />
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 1 }}>
            <TextField
              select
              fullWidth
              value={category}
              onChange={(e) => {
                const newCategory = e.target.value;
                setCategory(newCategory);
                if (onCategoryChange) {
                  onCategoryChange(newCategory);
                  setSearchQuery('');
                }
              }}
              size="small"
              SelectProps={{
                native: false,

                IconComponent: (props) => (
                  <KeyboardArrowDown {...props} sx={{ color: '#9ca3af', fontSize: 24 }} />
                ),
                renderValue: (selected: any) => {
                  const displayText =
                    selected && selected.length > 20 ? selected.substring(0, 20) + '...' : selected;

                  return (
                    <>
                      <span
                        style={{
                          fontFamily: 'Poppins',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#737373',
                          textAlign: 'left',
                        }}
                      >
                        Category :{' '}
                      </span>
                      <span
                        style={{
                          fontFamily: 'Poppins',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#000000',
                          textAlign: 'right',
                        }}
                      >
                        {displayText}
                      </span>
                    </>
                  );
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '14px',
                  '& fieldset': {
                    border: 'none !important',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingRight: '40px !important',
                },
              }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Current Process">Current Process</MenuItem>
              <MenuItem value="Expected Impact">Expected Impact</MenuItem>
              <MenuItem value="Specific Idea of Improvement">Specific Idea of Improvement</MenuItem>
              <MenuItem value="Key Performance Indicators">Key Performance Indicators</MenuItem>
            </TextField>
          </Box>

          {/* Sort By Dropdown */}
          <Box sx={{ mb: 3 }}>
            <TextField
              select
              fullWidth
              value={sortBy}
              onChange={(e) => {
                const newSortBy = e.target.value;
                setSortBy(newSortBy);
                if (onSortChange) {
                  onSortChange(newSortBy);
                }
              }}
              size="small"
              SelectProps={{
                native: false,
                IconComponent: (props) => (
                  <KeyboardArrowDown {...props} sx={{ color: '#9ca3af', fontSize: 24 }} />
                ),
                renderValue: (selected: any) => {
                  const sortLabels: Record<string, string> = {
                    newest_first: 'Newest First',
                    oldest_first: 'Oldest First',
                    name_asc: 'Name (A-Z)',
                    name_desc: 'Name (Z-A)',
                    most_intakes: 'Most Intakes',
                    least_intakes: 'Least Intakes',
                  };
                  const displayText = sortLabels[selected] || selected;

                  return (
                    <>
                      <span
                        style={{
                          fontFamily: 'Poppins',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#737373',
                          textAlign: 'left',
                        }}
                      >
                        Sort By :{' '}
                      </span>
                      <span
                        style={{
                          fontFamily: 'Poppins',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#000000',
                          textAlign: 'right',
                        }}
                      >
                        {displayText}
                      </span>
                    </>
                  );
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '14px',
                  '& fieldset': {
                    border: 'none !important',
                  },
                },

                '& .MuiOutlinedInput-input': {
                  padding: '8px 12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingRight: '40px !important',
                },
              }}
            >
              <MenuItem value="newest_first">Newest First</MenuItem>
              <MenuItem value="oldest_first">Oldest First</MenuItem>
              <MenuItem value="name_asc">Name Ascending (A-Z)</MenuItem>
              <MenuItem value="name_desc">Name Descending (Z-A)</MenuItem>
              <MenuItem value="most_intakes">Most used to Least</MenuItem>
              <MenuItem value="least_intakes">Least used to Most</MenuItem>
            </TextField>
          </Box>

          {/* Search Bar */}
          {/* <TextField
            fullWidth
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => {
              const newSearchQuery = e.target.value;
              setSearchQuery(newSearchQuery);
            }}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search sx={{ color: '#000000', fontSize: '20px' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                fontSize: '14px',
                '& fieldset': {
                  borderColor: '#e5e7eb',
                },
                '&:hover fieldset': {
                  borderColor: '#d1d5db',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                  borderWidth: '1px',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px',
                '&::placeholder': {
                  color: '#7B7B7B',
                },
              },
            }}
          /> */}

          {/* Create New Button */}
          {/* <Box
            onClick={() => {
              onNewChat();
              onNewIdeation();
            }}
            sx={{
              color: '#1A55F9',
              backgroundColor: '#E4EBFF',
              borderRadius: '8px',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: '#eff6ff',
              },
            }}
          >
            <Add fontSize="small" />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '15px',
              }}
            >
              Create New Knowledge
            </Typography>
          </Box> */}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 2,
            gap: 2,
          }}
        >
          <Tooltip title="Create New Knowledge" placement="right">
            <IconButton
              onClick={() => {
                onNewChat();
                onNewIdeation();
              }}
              size="small"
              sx={{
                color: '#1A55F9',
                border: '1px solid #1A55F9',
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#eff6ff' },
              }}
            >
              <Add fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Chat List */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          // match the horizontal padding of the header for consistent widths
          px: isCollapsed ? 1 : 2,
          py: 1,
          position: 'relative',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#d1d5db',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#9ca3af',
          },
        }}
      >
        {!isCollapsed && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sessions.map((session) => (
              <Box
                key={session.id}
                data-conversation-id={session.conversation_id}
                onMouseEnter={() => setHoveredSessionId(session.id)}
                onMouseLeave={() => setHoveredSessionId(null)}
                onClick={() => onSelectChat(session?.conversation_id, session?.field_type || '')}
                sx={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  p: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: '#ffffff',
                  borderColor: currentChatId === session.conversation_id ? '#1A55F9' : '#e5e7eb',
                  position: 'relative',
                  // '&:hover': {
                  //   backgroundColor: '#f9fafb',
                  //   borderColor: '#d1d5db',
                  // },
                }}
              >
                <Box>
                  {/* Title row with three-dots menu aligned space-between */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: '#313131',
                        fontSize: '15px',
                        mb: 1,
                        lineHeight: 1.4,
                        flex: 1,
                      }}
                    >
                      {session.use_case_title || 'Untitled Process'}
                    </Typography>

                    <Box
                      sx={{
                        position: 'relative',
                        alignItems: 'center',
                        display: 'flex',
                        flexShrink: 0,
                      }}
                    >
                      {hoveredSessionId === session.id && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, session)}
                          sx={{
                            color: '#6b7280',
                            padding: '2px',
                            '&:hover': {
                              backgroundColor: '#e5e7eb',
                            },
                          }}
                        >
                          <MoreHoriz fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {/* Meta info and intake row */}
                  <Box sx={{ flex: 1, pr: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#313131',
                        display: 'block',
                        fontSize: '11px',
                        fontWeight: 400,
                        fontFamily: 'Poppins',
                        mb: 0.5,
                      }}
                    >
                      {session?.updated_by
                        ? `updated by ${capitalizeName(session.updated_by)} `
                        : `created by ${capitalizeName(session.created_by)} `}
                      {formatTimestamp(session.created_at)}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#313131',
                          fontSize: '11px',
                          display: 'inline-block',
                          textWrap: 'nowrap',
                          fontWeight: 400,
                          fontFamily: 'Poppins',
                        }}
                      >
                        {mapProcessName(session.usedInProcess)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#313131',
                          fontSize: '11px',
                          fontWeight: 400,
                          fontFamily: 'Poppins',
                        }}
                      >
                        {' · '}
                        Used in
                      </Typography>
                      <Box
                        onMouseEnter={(e) => handleIntakeHover(e, session.ideation_intake)}
                        onMouseLeave={handleIntakeClose}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          // backgroundColor: '#ffffff',
                          color: '#313131',
                          borderRadius: '50%',
                          minWidth: '18px',
                          height: '18px',
                          fontSize: '11px',
                          fontWeight: 400,
                          fontFamily: 'Poppins',
                          px: 0.5,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        {session.ideation_intake?.length || 0} intake
                      </Box>
                    </Box>
                  </Box>

                  {/* Context Menu */}
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl) && selectedSession?.id === session.id}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    PaperProps={{
                      sx: {
                        borderRadius: '8px',
                        boxShadow:
                          '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        mt: 1,
                      },
                    }}
                  >
                    <MenuItem
                      onClick={handleRename}
                      sx={{ fontFamily: 'Poppins', fontSize: '14px' }}
                    >
                      Rename
                    </MenuItem>
                    <MenuItem
                      onClick={handleDelete}
                      sx={{ fontFamily: 'Poppins', fontSize: '14px', color: '#EF4444' }}
                    >
                      Delete
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
            ))}

            {/* Loading indicator or end message at the bottom */}
            {isLoadingMore && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 2,
                }}
              >
                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '14px' }}>
                  Loading more sessions...
                </Typography>
              </Box>
            )}
            {!isLoadingMore && hasReachedEnd && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: '#9ca3af', fontSize: '14px', fontStyle: 'italic' }}
                >
                  No more sessions available
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Intake Details Popover */}
      <Popover
        open={Boolean(intakeAnchorEl) && hoveredIntakes.length > 0}
        anchorEl={intakeAnchorEl}
        onClose={handleIntakeClose}
        disableRestoreFocus
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          pointerEvents: 'none',
        }}
        PaperProps={{
          sx: {
            pointerEvents: 'auto',
            backgroundColor: '#ffffff',
            mt: 0.1,
          },
          onMouseEnter: handleIntakePopoverEnter,
          onMouseLeave: handleIntakeClose,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '250px', p: 1 }}>
          {hoveredIntakes.map((intake, index) => (
            <Box
              key={intake.id || index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 0.75,
                backgroundColor: '#FFFFFF',
                borderRadius: '6px',
                //border: '1px solid #E5E7EB',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: 'Poppins',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#000000',
                  }}
                >
                  Ideation XPLR &gt; {intake?.level2_name || intake?.ideation_name || 'N/A'} &gt;{' '}
                  {intake.type || 'N/A'}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#6B7280',
                  ml: 2,
                }}
              >
                {intake.version || 'N/A'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>

      {/* Rename Modal */}
      <Modal
        open={openRenameModal}
        onClose={() => setOpenRenameModal(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '650px',
            outline: 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderRadius: '8px 8px 0 0',
              p: 2,
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#1A55F9',
            }}
          >
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#FFFFFF',
                fontFamily: 'Poppins',
              }}
            >
              Rename Session
            </Typography>
            <IconButton
              onClick={() => setOpenRenameModal(false)}
              size="small"
              sx={{
                color: '#FFFFFF',
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ p: 2.5 }}>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 400,
                color: '#000000',
                mb: 1,
                fontFamily: 'Poppins',
              }}
            >
              Session name
            </Typography>
            <TextField
              fullWidth
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Untitled"
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '16px',
                  fontFamily: 'Poppins',
                  color: '#000000',
                  '& fieldset': {
                    borderColor: '#e5e7eb',
                  },
                  '&:hover fieldset': {
                    borderColor: '#d1d5db',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1A55F9',
                    borderWidth: '1px',
                  },
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              p: 2.5,
              borderTop: '1px solid #e5e7eb',
            }}
          >
            <Box
              onClick={() => setOpenRenameModal(false)}
              sx={{
                px: 3,
                py: 1,
                borderRadius: '6px',
                cursor: 'pointer',
                background: '#f3f4f6',
                color: '#000000',
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: 'Poppins',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: '#e5e7eb',
                },
              }}
            >
              Cancel
            </Box>

            <Box
              onClick={handleRenameConfirm}
              sx={{
                px: 3,
                py: 1,
                borderRadius: '6px',
                cursor: 'pointer',
                background: '#1A55F9',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: 'Poppins',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: '#1543d1',
                },
              }}
            >
              Rename
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: 24,
            p: 0,
            minWidth: 700,
            maxWidth: 700,
            outline: 'none',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              backgroundColor: '#1A55F9',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#fff' }}>
              Delete Session
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpenDeleteModal(false)}
              sx={{
                color: '#fff',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontSize: '16px', color: '#333' }}>
              Do you want to delete this session?
            </Typography>
          </Box>

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              p: 2,
              borderTop: '1px solid #E5E5E5',
            }}
          >
            <Button
              onClick={() => setOpenDeleteModal(false)}
              sx={{
                color: '#666',
                textTransform: 'none',
                fontSize: '14px',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              No
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: '#1A55F9',
                textTransform: 'none',
                fontSize: '14px',
                '&:hover': { backgroundColor: '#1547D9' },
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Modal>
      <SearchModal
        openSearchModal={openSearchModal}
        setOpenSearchModal={setOpenSearchModal}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        chatsData={sessions}
        onSelectChat={onSelectChat}
        isLoadingMore={isLoadingMore}
        hasReachedEnd={hasReachedEnd}
        onLoadMore={onLoadMore}
        onSearchChange={onSearchChange}
      />

      {/* Collapse/Expand Button at Bottom */}
      <Box
        sx={{
          p: 2,
          display: 'flex',

          justifyContent: isCollapsed ? 'center' : 'flex-start',
          borderTop: '1px solid #e5e7eb',
        }}
      >
        <Tooltip title={isCollapsed ? 'Expand' : 'Collapse'} placement="right">
          <IconButton
            onClick={toggleCollapse}
            size="small"
            sx={{
              color: '#6b7280',
              // border: '1px solid #e5e7eb',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#f9fafb',
                borderColor: '#d1d5db',
              },
            }}
          >
            {isCollapsed ? (
              <img src="/images/frontbtndata.svg" alt="" />
            ) : (
              <img src="/images/backbtndata.svg" alt="" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ChatListingSidebar;
